import { VideoAPIService } from './AxiosAPIService';
import {
  AlertVideoResponse,
  Video,
  VideoResponse,
  VideoAggregationResponse,
  VideoTimeline,
  PostVideoRequest,
  VideoTimelineRequest,
  StreamConfigResponse,
  ActiveCustomVodLimitResponse,
  DeviceVideoSettings,
  DeviceVideoSettingsPayload,
  DeviceVideoSettingsResponse,
  DeleteVideoRequest,
  DeleteVideosRequest,
  DeviceVideoErrResponse,
} from '../types/Videos';
import { logger } from './SharedService';

const VIDEO_ENDPOINT = '/videos';

export const getVideos = async (fleetId: string, query: string): Promise<VideoResponse> => {
  const { data } = await VideoAPIService.api
    .get<VideoResponse>(`/fleets/${fleetId}/videos${query}`);
  return data;
};

export const getVideosAggregation = async (fleetId: string, query: string): Promise<VideoAggregationResponse> => {
  const { data } = await VideoAPIService.api
    .get<VideoAggregationResponse>(`/fleets/${fleetId}/videos/aggregation${query}`);
  return data;
};

export const getVideoErr = async (deviceId: string): Promise<DeviceVideoErrResponse> => {
  const { data } = await VideoAPIService.api
    .get<DeviceVideoErrResponse>(`/getVideoErr/${deviceId}/videos`);
  return data;
};

export const getDeviceErr = async (deviceId: string): Promise<DeviceVideoErrResponse> => {
  const { data } = await VideoAPIService.api
    .get<DeviceVideoErrResponse>(`/getDeviceErr/${deviceId}`);
  return data;
};

export const getVideoById = async (id: string, fleetId: string): Promise<Video> => {
  const { data } = await VideoAPIService.api
    .get<Video>(`/fleets/${fleetId}/videos/${id}`);
  return data;
};

const getDeleteVideoPath = ({ fleetId, id, vodRequesterUserId }: DeleteVideoRequest): string => (
  vodRequesterUserId
    ? `/fleets/${fleetId}/videos/${id}/${vodRequesterUserId}`
    : `/fleets/${fleetId}/videos/${id}`
);

const getVideoServiceErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const { response } = error as { response?: { data?: { message?: string | string[] } } };
    const message = response?.data?.message;
    if (Array.isArray(message)) {
      return message.join(', ');
    }
    if (typeof message === 'string' && message.length > 0) {
      return message;
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

export const deleteVideo = async (payload: DeleteVideoRequest): Promise<Video> => {
  try {
    const { data } = await VideoAPIService.api
      .delete<Video>(getDeleteVideoPath(payload));
    return data;
  } catch (error) {
    return Promise.reject(new Error(getVideoServiceErrorMessage(error, 'Failed to delete video')));
  }
};

export const deleteVideos = async (payload: DeleteVideosRequest): Promise<Video[]> => {
  try {
    const deletePromises = payload.items.map(async (item) => {
      const { data } = await VideoAPIService.api.delete<Video>(getDeleteVideoPath(item));
      return data;
    });

    const results = await Promise.all(deletePromises);
    return results;
  } catch (error) {
    return Promise.reject(new Error(getVideoServiceErrorMessage(error, 'Failed to delete video(s)')));
  }
};

export const getAlertVideos = async (id: string, fleetId: string): Promise<AlertVideoResponse> => {
  const { data } = await VideoAPIService.api
    .get<AlertVideoResponse>(`/fleets/${fleetId}/alerts/${id}${VIDEO_ENDPOINT}`);
  return data;
};

export const getDeviceVideos = async (id: string, fleetId: string): Promise<AlertVideoResponse> => {
  const { data } = await VideoAPIService.api
    .get<AlertVideoResponse>(`/fleets/${fleetId}/devices/${id}${VIDEO_ENDPOINT}`);
  return data;
};

export const getDeviceVideoTimelines = async (payload: VideoTimelineRequest): Promise<VideoTimeline> => {
  const { fleetId, deviceId, date } = payload;
  const currentDate = new Date(date);
  const startDate = currentDate.getTime() / 1000;
  currentDate.setHours(23, 59, 59, 999);
  const endDate = currentDate.getTime() / 1000;

  const { data } = await VideoAPIService.api
    .get<VideoTimeline>(
    `/fleets/${fleetId}/devices/${deviceId}${VIDEO_ENDPOINT}/timelines?tsSecEpochStart=${startDate}&tsSecEpochEnd=${endDate}`,
  );
  return data;
};

export const getStreamConfig = async (id: string, fleetId: string, cameraId: string | number, VPNStatus: boolean): Promise<StreamConfigResponse> => {
  const { data } = await VideoAPIService.api
    .get<StreamConfigResponse>(`/streams/fleets/${fleetId}/devices/${id}/cameras/${cameraId}/credentials/${VPNStatus}`);
  return data;
};

export const getActiveCustomVodLimit = async (
  fleetId: string,
  deviceId: string,
): Promise<ActiveCustomVodLimitResponse> => {
  const { data } = await VideoAPIService.api
    .get<ActiveCustomVodLimitResponse>(
    `/fleets/${fleetId}/devices/${deviceId}${VIDEO_ENDPOINT}/active-custom-vod-limit`,
  );
  return data;
};

export const postVideoRequest = async (payload: PostVideoRequest): Promise<Video[]> => {
  const {
    deviceId,
    fleetId,
    from,
    to,
    cameraIds,
    requestedFrom,
    requestedTo,
    fulfillmentStatus,
    requestedByUserId,
  } = payload;

  const { data } = await VideoAPIService.api
    .post<Video[]>(`/fleets/${fleetId}/devices/${deviceId}${VIDEO_ENDPOINT}`, {
    from,
    to,
    cameraIds,
    requestedFrom,
    requestedTo,
    fulfillmentStatus,
    requestedByUserId,
  })
    .catch((e) => Promise.reject(e.response?.data ?? e));
  return data;
};

export const getDeviceVideoFleetSettings = async (
  fleetId: string,
): Promise<DeviceVideoSettings> => {
  logger.info('GET', 'REQUEST', 'getDeviceVideoFleetSettings', `/device-settings/fleets/${fleetId}`);
  const { data } = await VideoAPIService.api
    .get<DeviceVideoSettings>(`/device-settings/fleets/${fleetId}`);
  logger.info('GET', 'RESPONSE', 'getDeviceVideoFleetSettings', data);
  return data;
};

export const getDeviceVideoSettings = async (
  id: string | undefined,
  fleetId: string,
): Promise<DeviceVideoSettings> => {
  logger.info('GET', 'REQUEST', 'getDeviceVideoSettings', `/device-settings/fleets/${fleetId}/devices/${id}`);
  const { data } = await VideoAPIService.api
    .get<DeviceVideoSettings>(`/device-settings/fleets/${fleetId}/devices/${id}`);
  logger.info('GET', 'RESPONSE', 'getDeviceVideoSettings', data);
  return data;
};

export const postDeviceVideoFleetSettings = async ({
  fleetId,
  videoStreamEnable,
  isActive,
}: DeviceVideoSettingsPayload): Promise<DeviceVideoSettingsResponse> => {
  logger.info('POST', 'REQUEST', 'postDeviceVideoFleetSettings', `/device-settings/fleets/${fleetId}`, videoStreamEnable, isActive);
  const { data } = await VideoAPIService.api
    .post<DeviceVideoSettingsResponse>(`/device-settings/fleets/${fleetId}`, { videoStreamEnable, isActive })
    .catch((e) => Promise.reject(e.response.data));
  logger.info('POST', 'RESPONSE', 'postDeviceVideoFleetSettings', data);
  return data;
};

export const postDeviceVideoSettings = async ({
  fleetId,
  deviceId,
  videoStreamEnable,
}: DeviceVideoSettingsPayload): Promise<DeviceVideoSettingsResponse> => {
  logger.info('POST', 'REQUEST', 'postDeviceVideoSettings', `/device-settings/fleets/${fleetId}/devices/${deviceId}`, videoStreamEnable);
  const { data } = await VideoAPIService.api
    .post<DeviceVideoSettingsResponse>(`/device-settings/fleets/${fleetId}/devices/${deviceId}`, { videoStreamEnable })
    .catch((e) => Promise.reject(e.response.data));
  logger.info('POST', 'RESPONSE', 'postDeviceVideoSettings', data);
  return data;
};
